import { type HTMLAttributes, forwardRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Card, Dialog } from "widgets";

import type { ITableSpecWithContext } from "../../content-browser/ContentBrowserTableView.js";
import ColumnSelectionButton from "../../core/ColumnSelectionButton.js";
import DataTableContainer from "../../core/grid/DataTableContainer.js";
import { type ISelectionModel, createSelectionModel } from "@/app-utils/selection-model.ts";
import { organismToMutantGenome } from "@/app-utils/variant.ts";
import type { Organism, VariantAnalysisGroup } from "@/graphql.ts";
import {
  type IMutantGenome,
  getMutantGenomesByAnalysisGroup,
  loadVariantRelatedGenomes,
  setSelectedMutantGenomeList,
} from "@/redux/modules/genome-browser.ts";
import {
  type ITableData,
  createTableSpecForAnalysisGroupParent,
  createUniqueKeyFromTableSpec,
  getTableDataSlice,
  getTableQuerySpecWithSelection,
} from "@/redux/modules/table-data.ts";
import type { IAppState } from "@/redux/reducer.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";

export interface IVariantsRelatedGenomesDialogProps {
  closeFn: () => void;
  variantTable: ITableData;
  selectedVariantIDs: number[];
  dialogProps: HTMLAttributes<HTMLElement>;
  underlayProps: HTMLAttributes<HTMLElement>;
}

const VariantsRelatedGenomesDialog = forwardRef<HTMLDivElement, IVariantsRelatedGenomesDialogProps>(
  function VariantsRelatedGenomesDialog(props, ref) {
    const dispatch = useDispatch<ReduxDispatch>();
    const [analysisGroupTableSpec, setAnalysisGroupTableSpec] = useState<ITableSpecWithContext>();
    const [isSubmitting, setSubmitting] = useState<boolean>(false);

    const analysisGroupMutantGenomes = useSelector<IAppState, IMutantGenome[]>(
      (state) => state.genomeBrowser.selectedMutantGenomes,
    );
    const maxSelectableMutantGenomesAsVariantRestriction: number = useSelector<IAppState, number>((state) =>
      Number.parseInt(state.config.configuration?.application?.maxVisibleVariantGenomes, 10),
    );

    const [error, setError] = useState<string>();
    const variantAnalysisGroup = useSelector<IAppState, VariantAnalysisGroup>(
      (state) => state.genomeBrowser.genome.variantAnalysisGroups?.[0],
    );
    const analysisGroupSelectionModel = useSelector<IAppState, ISelectionModel>((state) => {
      if (analysisGroupTableSpec) {
        const analysisGroupTableData = getTableDataSlice(state.tableData, analysisGroupTableSpec);
        return analysisGroupTableData?.selectionModel;
      }
      return null;
    });

    useEffect(() => {
      dispatch(
        loadVariantRelatedGenomes({
          ...getTableQuerySpecWithSelection(props.variantTable, props.selectedVariantIDs),
        }),
      )
        .then((genomeIDs) => {
          const analysisGroupTableSpec = {
            ...createTableSpecForAnalysisGroupParent(variantAnalysisGroup),
            context: "track-custom",
            restrictions: {
              refGenomeId: props.variantTable.tableQuerySpec.restrictions.refGenomeId,
              selection: {
                selectedResultIds: genomeIDs,
              },
            },
          };
          setAnalysisGroupTableSpec(analysisGroupTableSpec);
        })
        .catch((error: Error) => setError(error?.message));
    }, []);

    useEffect(() => {
      if (analysisGroupSelectionModel?.selectedIds.length > maxSelectableMutantGenomesAsVariantRestriction) {
        setError(`You cannot add more than ${maxSelectableMutantGenomesAsVariantRestriction} genomes to the track`);
        return;
      }
      setError(null);
    }, [analysisGroupSelectionModel?.selectedIds]);

    function onAddToTrack() {
      setSubmitting(true);
      void dispatch(
        getMutantGenomesByAnalysisGroup(variantAnalysisGroup, analysisGroupSelectionModel?.selectedIds as number[]),
      ).then((genomes: Organism[]) => {
        const mutants = genomes.filter((mutantGenome) => !mutantGenome.isReference).map(organismToMutantGenome);
        dispatch(setSelectedMutantGenomeList(mutants));
        props.closeFn();
      });
    }

    return (
      <Dialog
        title={"Genomes with Selected Variants"}
        actions={[
          <ColumnSelectionButton
            portalContext={createUniqueKeyFromTableSpec(analysisGroupTableSpec)}
            key={"variants-track-variant-genome-dialog"}
          />,
        ]}
        mainButton={{
          label: "Add to Track",
          onPress: () => onAddToTrack(),
          isDisabled: !!error,
          isLoading: isSubmitting,
        }}
        errorCallout={{ message: error }}
        closeFn={props.closeFn}
        ref={ref}
        domProps={props.dialogProps}
        underlayProps={{ ...props.underlayProps, style: { ...props.underlayProps.style, zIndex: 499 } }}
        fullscreen={true}
        noRestoreFocus
        noContainedFocus
        contentStyle={{ padding: 0 }}
      >
        <Card
          fillHeight
          noBorder
          noPadding
          style={{
            borderTop: "1px solid #d8e4ed",
          }}
        >
          {analysisGroupTableSpec && (
            <DataTableContainer
              key={createUniqueKeyFromTableSpec(analysisGroupTableSpec)}
              tableSpec={analysisGroupTableSpec}
              tableInitializationOptions={{
                initialSelectionModel: createSelectionModel(analysisGroupMutantGenomes.map((genome) => genome.id)),
              }}
            />
          )}
        </Card>
      </Dialog>
    );
  },
);

export { VariantsRelatedGenomesDialog };
